// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSelector } from '@reduxjs/toolkit';
import { selectCompanyEquipmentTypes } from '../companies/companiesSelectors';

export const selectStudios = state => state.studio.studios;
export const selectStudio = state => state.studio.studio;
export const selectStudioUuid = state => state.studio.studio?.studio_uuid;
export const selectStudioEquipment = state => state.studio.studio?.studio_equipment || [];

const ADD_EQUIPMENT_BLACKLIST = ['Preset Manager', 'Thumbnail Generator'];

export const selectStudioEquipmentOptionsTypes = createSelector(
  [selectStudioEquipment, selectCompanyEquipmentTypes],
  (studioEquipment, equipmentTypes) => {
    if (!studioEquipment.length || !equipmentTypes.length) {
      return [];
    }

    // Group company equipment by equip_type_uuid
    const equipmentByType = studioEquipment.reduce((acc, equipment) => {
      const { co_equip: eq } = equipment || {};
      const typeUuid = eq?.equip_type_uuid;
      if (!typeUuid) {
        return acc;
      }
      if (ADD_EQUIPMENT_BLACKLIST.includes(eq?.name)) {
        return acc;
      }

      if (!acc[typeUuid]) {
        acc[typeUuid] = [];
      }
      acc[typeUuid].push(equipment);

      return acc;
    }, {});

    // Build children (equipment types)
    const typeChildren = equipmentTypes
      .map(type => {
        const children = (equipmentByType[type.equip_type_uuid] ?? []).map(equipment => ({
          item: equipment,
          id: equipment.co_equip_uuid,
          name: equipment.co_equip?.name,
        }));

        return {
          id: type.equip_type_uuid,
          name: type.name,
          placeholder: `new ${type.name}...`,
          children,
        };
      })
      .filter(type => type.children.length > 0 && !ADD_EQUIPMENT_BLACKLIST.includes(type.name));

    if (!typeChildren.length) {
      return [];
    }
    return typeChildren;
  }
);

// Selector to get options for studio equipment to add in the React Flow
// for each equipment, return { value: co_equip_uuid, label: name }
export const selectStudioEquipmentOptions = createSelector(
  [selectStudioEquipment],
  studioEquipment =>
    studioEquipment
      .map(equipment => ({
        value: equipment.co_equip_uuid,
        label: equipment.co_equip?.name,
      }))
      .filter(e => !ADD_EQUIPMENT_BLACKLIST.includes(e.label))
);
